import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  useSelectMenu,
  SelectMenuProps,
  OptionType,
  Icon,
} from '@fullstory/components';
import { ResourceDisplayPane } from '../ResourceDisplayPane';
import { ResourceMultiSelectMenu } from './ResourceMultiSelectMenu';
import css from './index.module.scss';
import { Metadata, MetadataDisplay } from '../MetadataDisplay';
import { intl } from '@fullstory/intl';
import { ViewOnlyTooltip } from '../ViewOnlyTooltip';

export { ResourceMultiSelectMenu };
export type { OptionType };

export type ResourceSelectMenuItemType =
  | 'Option'
  | 'Funnel'
  | 'Metric'
  | 'hostname'
  | 'Segment'
  | 'Element'
  | 'Page'
  | 'CustomEvent'
  | 'Space'
  | 'attribute'
  | 'Connection'
  | 'propertyRuleSource';

interface ResourceSelectMenuProps<T>
  extends SelectMenuProps<T>,
    Pick<React.ComponentProps<typeof MetadataDisplay>, 'onModifyOwner'> {
  /**
   * String that identifies the type of item being filtered. (e.g. "Metric")
   */
  itemType: ResourceSelectMenuItemType;
  onCreate?: () => void;
  onEdit?: (option: OptionType<T>) => void;
  renderDefinition?: (option: OptionType<T>) => ReactNode;
  getMetadata?: (option: OptionType<T>) => Metadata | null;
  canAccess?: boolean;
  createButtonProps?: React.ComponentPropsWithoutRef<typeof Button>;
  /** Optional CTA rendered in the header */
  renderAction?: (option: OptionType<T>) => ReactNode;
  canEdit?: (option: OptionType<T>) => boolean | 'loading';
}

const resourceSelectMenuMessages: {
  [key in ResourceSelectMenuItemType]: { createText: string };
} = {
  Element: {
    createText: intl.formatMessage({ defaultMessage: 'Create Element', id: 'TWGQeP' }),
  },
  Funnel: {
    createText: intl.formatMessage({ defaultMessage: 'Create Funnel', id: 'aWjWtn' }),
  },
  Metric: {
    createText: intl.formatMessage({ defaultMessage: 'Create Metric', id: 'QHRG0b' }),
  },
  Option: {
    createText: intl.formatMessage({ defaultMessage: 'Create Option', id: 'Kb1DBo' }),
  },
  Page: { createText: intl.formatMessage({ defaultMessage: 'Create Page', id: 'XGRBwY' }) },
  Segment: {
    createText: intl.formatMessage({ defaultMessage: 'Create Segment', id: 'GaaNiU' }),
  },
  Space: {
    createText: intl.formatMessage({ defaultMessage: 'Create Space', id: 'v3aFf1' }),
  },
  hostname: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom hostname', id: 'sAlOVS' }),
  },
  CustomEvent: {
    createText: intl.formatMessage({ defaultMessage: 'Create Event', id: 'pSavjx' }),
  },
  attribute: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom attribute', id: 'GRiz/A' }),
  },
  Connection: {
    createText: intl.formatMessage({ defaultMessage: 'Create connection', id: 'kBOxUj' }),
  },
  propertyRuleSource: {
    createText: '', // not used
  },
};

export function ResourceSelectMenu<T>({
  options,
  testId = 'resource-select-menu',
  itemType,
  onCreate,
  onEdit,
  renderDefinition,
  getMetadata,
  onModifyOwner,
  canEdit: canEditFunc,
  createButtonProps,
  renderAction,
  ...selectMenuProps
}: ResourceSelectMenuProps<T>) {
  const [highlighted, setHighlighted] = useState<OptionType<T> | null>(null);
  const { Select, selectProps, Filter, filterRef, filterProps, Options, optionsProps } =
    useSelectMenu({
      options,
      alwaysHovered: true,
      testId,
      onHighlight: setHighlighted,
      ...selectMenuProps,
    });

  const messages = resourceSelectMenuMessages[itemType];
  const action = highlighted ? renderAction?.(highlighted) : undefined;

  const [canEdit, setCanEdit] = useState<boolean | 'loading'>(false);
  useEffect(() => {
    if (highlighted) {
      setCanEdit(canEditFunc ? canEditFunc(highlighted) : true);
    }
  }, [canEditFunc, highlighted]);

  const onFirstUpdate = useCallback(() => filterRef.current?.focus(), [filterRef]);

  return (
    <Select
      data-component="ResourceSelectMenu"
      contentWidth="auto"
      onFirstUpdate={onFirstUpdate}
      {...selectProps}
    >
      <Flex>
        <Flex column alignItems="stretch" justifyContent="stretch" className={css.menu}>
          {options.length > 10 && (
            <Box flexShrink={0}>
              <Filter ref={filterRef} {...filterProps} />
            </Box>
          )}
          <Options hoistSelected {...optionsProps} />
          {onCreate && (
            <Box flexShrink={0} minHeight={66}>
              <Divider />
              <Box display="flex" alignItems="center" height="100%">
                <ViewOnlyTooltip isViewOnly={createButtonProps?.disabled} asChild>
                  <Box width="100%">
                    <Button
                      variant="minimal"
                      fullWidth
                      {...createButtonProps}
                      onClick={() => {
                        onCreate();
                        // assuming the onCreate triggers something outside of the select menu,
                        // we now want to close the menu
                        selectProps.onClose();
                      }}
                    >
                      <Flex gap={8}>
                        <Icon name="plus" />
                        {messages.createText}
                      </Flex>
                    </Button>
                  </Box>
                </ViewOnlyTooltip>
              </Box>
            </Box>
          )}
        </Flex>
        {highlighted &&
          renderDefinition &&
          renderDefinition(highlighted) &&
          // The highlighted item doesn't get reset when no options match the filter.
          // So, if there are no options we should not show the pane.
          optionsProps.options.length > 0 && (
            <ResourceDisplayPane>
              <ResourceDisplayPane.Header
                label={highlighted.label}
                icon={highlighted.icon}
                onOpen={
                  onEdit
                    ? () => {
                        onEdit(highlighted);
                        // [alvaromndz] same assumption as `onCreate` above...
                        selectProps.onClose();
                      }
                    : undefined
                }
                canAccess={canEdit}
                actionComponent={action}
              />
              <ResourceDisplayPane.Body>{renderDefinition(highlighted)}</ResourceDisplayPane.Body>
              {getMetadata && getMetadata(highlighted) && (
                <ResourceDisplayPane.Footer
                  metadata={getMetadata(highlighted)!}
                  onModifyOwner={onModifyOwner}
                />
              )}
            </ResourceDisplayPane>
          )}
      </Flex>
    </Select>
  );
}
